import { useEffect, useState } from 'react'

export default function AdminUsers() {
  const [users, setUsers]         = useState([])
  const [selected, setSelected]   = useState(null)
  const [form, setForm]           = useState(null)
  const [mode, setMode]           = useState('view')   // view | new | edit
  const [error, setError]         = useState('')
  const [success, setSuccess]     = useState('')
  const [search, setSearch]       = useState('')       // <-- SØKEFELT

  const API = process.env.REACT_APP_API_URL

  useEffect(() => { fetchUsers() }, [])

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API}/users`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      })
      if (!res.ok) throw new Error()
      setUsers(await res.json())
    } catch {
      setError('Kunne ikke hente brukere')
    }
  }

  const handleNew = () => {
    setForm({
      name: '',
      email: '',
      phone: '',
      address: '',
      postal_code: '',
      city: '',
      password: '',
      isBusiness: false,
      orgNumber: ''
    })
    setSelected(null)
    setMode('new')
    setError(''); setSuccess('')
  }

  const handleEdit = () => {
    setForm({
      ...selected,
      postal_code: selected?.postal_code ?? '',
      city: selected?.city ?? ''
    })
    setMode('edit')
    setError(''); setSuccess('')
  }

  const handleCancel = () => {
    setForm(null)
    setMode('view')
    setError(''); setSuccess('')
  }

  const handleChange = e => {
    const { name, value, type, checked } = e.target
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleFormSubmit = async e => {
    e.preventDefault()
    setError(''); setSuccess('')

    // enkel validering av postnr
    if (form.postal_code && !/^\d{4}$/.test(String(form.postal_code))) {
      setError('Postnummer må være 4 siffer')
      return
    }

    const url    = mode === 'edit'
      ? `${API}/users/update/${form.id}`
      : `${API}/users`
    const method = mode === 'edit' ? 'PUT' : 'POST'

    try {
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(form)
      })
      const result = await res.json()
      if (!res.ok) throw new Error(result.error || 'Feil ved lagring')

      setSuccess(mode === 'edit' ? 'Bruker oppdatert' : 'Ny bruker opprettet')
      setForm(null)
      setMode('view')
      setSelected(null)
      fetchUsers()
    } catch (err) {
      setError(err.message)
    }
  }

  const handleDelete = async () => {
    if (!selected) return
    if (!window.confirm(`Slett bruker «${selected.name}»?`)) return

    try {
      const res = await fetch(`${API}/users/${selected.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      if (!res.ok) throw new Error('Feil ved sletting')
      setSuccess('Bruker slettet')
      setSelected(null)
      fetchUsers()
    } catch (err) {
      setError(err.message)
    }
  }

  // SØK - filtrer brukere basert på navn eller e-post
  const filteredUsers = users.filter(u => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (u.name || '').toLowerCase().includes(s) ||
      (u.email || '').toLowerCase().includes(s)
    );
  });

  return (
    <div className="w-full min-h-[500px] flex gap-6">
      {/* VENSTRE: Liste */}
      <div className="w-1/2 pr-6 border-r border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-[#000099]">Brukere</h2>
          <button
            onClick={handleNew}
            className="bg-[#000099] text-white px-4 py-1 rounded hover:bg-blue-800 transition"
          >
            + Ny
          </button>
        </div>
        {/* Søkefelt */}
        <input
          type="text"
          placeholder="Søk etter navn eller e-post..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full mb-3 px-3 py-2 border border-gray-300 rounded focus:ring focus:ring-[#000099] text-sm"
        />
        {error && <p className="text-red-500 mb-3">{error}</p>}
        <ul className="space-y-3 overflow-y-auto max-h-[500px]">
          {filteredUsers.map(u => (
            <li
              key={u.id}
              onClick={() => { setSelected(u); setForm(null); setMode('view'); setError(''); setSuccess('') }}
              className={`
                p-4 border border-gray-200 rounded hover:bg-gray-100 cursor-pointer transition
                ${selected?.id === u.id
                  ? "border-[#000099] bg-blue-50"
                  : "border-gray-100"}
              `}
            >
              <div className="font-medium">{u.name}</div>
              <div className="text-sm text-gray-600 break-all">{u.email}</div>
            </li>
          ))}
        </ul>
      </div>
      {/* HØYRE: Detaljer eller Skjema */}
      <div className="w-1/2 pl-6">
        <h2 className="text-2xl font-bold text-[#000099] mb-4">
          {mode === 'new'
            ? 'Ny bruker'
            : mode === 'edit'
            ? 'Rediger bruker'
            : 'Detaljer'}
        </h2>

        {success && <p className="text-green-600 mb-3">{success}</p>}

        {(mode === 'new' || mode === 'edit') && (
          <form onSubmit={handleFormSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Navn <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                value={form.name || ''}
                onChange={handleChange}
                className="mt-1 w-full border border-gray-300 px-3 py-2 rounded focus:ring focus:ring-[#000099]"
                required
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                E-post <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={form.email || ''}
                onChange={handleChange}
                className="mt-1 w-full border border-gray-300 px-3 py-2 rounded focus:ring focus:ring-[#000099]"
                required
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                Telefon
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                title="Telefon må være 8 siffer"
                value={form.phone || ''}
                onChange={handleChange}
                className="mt-1 w-full border border-gray-300 px-3 py-2 rounded focus:ring focus:ring-[#000099]"
              />
            </div>

            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                Adresse
              </label>
              <input
                id="address"
                name="address"
                type="text"
                value={form.address || ''}
                onChange={handleChange}
                className="mt-1 w-full	border border-gray-300 px-3 py-2 rounded focus:ring focus:ring-[#000099]"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="postal_code" className="block text-sm font-medium text-gray-700">
                  Postnr
                </label>
                <input
                  id="postal_code"
                  name="postal_code"
                  type="text"
                  value={form.postal_code ?? ''}
                  onChange={handleChange}
                  pattern="\d{4}"
                  title="Postnummer må være 4 siffer"
                  className="mt-1 w-full border border-gray-300 px-3 py-2 rounded focus:ring focus:ring-[#000099]"
                />
              </div>
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                  Sted
                </label>
                <input
                  id="city"
                  name="city"
                  type="text"
                  value={form.city ?? ''}
                  onChange={handleChange}
                  className="mt-1 w-full border border-gray-300 px-3 py-2 rounded focus:ring focus:ring-[#000099]"
                />
              </div>
            </div>

            <div>
              <label htmlFor="isBusiness" className="block text-sm font-medium text-gray-700">
                Bedriftskunde
              </label>
              <input
                id="isBusiness"
                name="isBusiness"
                type="checkbox"
                checked={!!form.isBusiness}
                onChange={handleChange}
                className="mt-1"
              />
              <span className="ml-2">Registrer som bedrift</span>
            </div>
            {form.isBusiness && (
              <div>
                <label htmlFor="orgNumber" className="block text-sm font-medium text-gray-700">
                  Organisasjonsnummer
                </label>
                <input
                  id="orgNumber"
                  name="orgNumber"
                  type="text"
                  value={form.orgNumber || ''}
                  onChange={handleChange}
                  className="mt-1 w-full border border-gray-300 px-3 py-2 rounded focus:ring focus:ring-[#000099]"
                  required
                />
              </div>
            )}
            {mode === 'new' && (
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Passord <span className="text-red-500">*</span>
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={form.password || ''}
                  onChange={handleChange}
                  className="mt-1 w-full	border border-gray-300 px-3 py-2 rounded focus:ring focus:ring-[#000099]"
                  required
                />
              </div>
            )}
            <div className="flex gap-4">
              <button type="submit" className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 transition">
                Lagre
              </button>
              <button type="button" onClick={handleCancel} className="bg-gray-300 px-6 py-2 rounded hover:bg-gray-400 transition">
                Avbryt
              </button>
            </div>
          </form>
        )}

        {/* Vis / Rediger / Slett */}
        {mode === 'view' && selected && (
          <div className="space-y-3 text-gray-800">
            <p><strong>Navn:</strong> {selected.name}</p>
            <p><strong>E-post:</strong> {selected.email}</p>
            <p><strong>Telefon:</strong> {selected.phone}</p>
            <p><strong>Adresse:</strong> {selected.address}</p>
            <p><strong>Postnr/Sted:</strong> {[selected.postal_code, selected.city].filter(Boolean).join(' ') || '–'}</p>
            <p><strong>Rolle:</strong> {selected.role}</p>
            <p>
              <strong>Kundetype:</strong>{' '}
              {selected.isBusiness ? (
                <>
                  Bedrift<br />
                  <strong>Org.nr:</strong> {selected.orgNumber}
                </>
              ) : (
                "Privat"
              )}
            </p>
            <div className="flex gap-4 pt-4">
              <button onClick={handleEdit} className="bg-yellow-500 text-white px-6 py-2 rounded hover:bg-yellow-600 transition">
                Rediger
              </button>
              <button onClick={async () => {
                if (!window.confirm(`Slett bruker «${selected.name}»?`)) return
                const res = await fetch(`${API}/users/${selected.id}`, {
                  method: 'DELETE',
                  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                })
                if (!res.ok) return setError('Feil ved sletting')
                setSuccess('Bruker slettet'); setSelected(null); fetchUsers()
              }} className="bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700 transition">
                Slett
              </button>
            </div>
          </div>
        )}

        {mode === 'view' && !selected && (
          <p className="text-gray-600">Velg en bruker for detaljer.</p>
        )}
      </div>
    </div>
  )
}
